/**
 * Sprint 90/91 — Leer lo que el cliente escribió en el chat y **proponer** el pedido.
 *
 * El 90% de las ventas nace en un mensaje de WhatsApp que hoy se teclea a mano, 50 a 150 veces al
 * día. Este módulo no adivina: **propone lo que reconoce y pregunta lo que no**.
 *
 * ## La regla que gobierna el archivo
 *
 * Un pedido mal interpretado que se va a la parrilla cuesta un platillo, un cliente y una llamada
 * incómoda. Un pedido a medias cuesta un clic. Ante la duda: **preguntar, nunca completar**.
 *
 * ## Lo que cambió en el Sprint 91, y por qué importa
 *
 * La primera versión comparaba contra **renglones del catálogo**, y el dueño del producto vio el
 * problema antes que yo:
 *
 * > *"Pensando en el menú del negocio… ¿notas algo extraño en el ejemplo? Sirve para demostrar que
 * > le trasladaremos un problema al negocio."*
 *
 * El catálogo guarda "Frijoles Charros 1 litro" y "Frijoles Charros 1/2 litro" como **dos productos
 * distintos**, porque así se descuenta el inventario (Sprint 82). Pero para el cliente —y para el
 * negocio— eso es **una sola cosa que se vende en dos tamaños**. Comparando renglón por renglón,
 * *"2 frijoles charros"* salía ambiguo: una pregunta que **no nace del cliente sino de nuestro
 * modelo de datos**, y que pagaba quien atiende, 150 veces al día.
 *
 * Ahora se interpreta contra el **grupo de venta** (Sprint 84). Un grupo con un solo tamaño no
 * pregunta nada; uno con varios pregunta **el tamaño**, que es la misma pregunta que el negocio le
 * haría al cliente por teléfono.
 *
 * ## Los apodos
 *
 * El cliente nunca escribe el nombre del catálogo: escribe *"una coca grande"*, *"2 espadas"*,
 * *"frijolitos"*. El arreglo fácil sería pedirle al negocio que renombre su menú para que el
 * programa entienda — o sea, que el negocio sirva al software. En vez de eso, el sistema **aprende
 * de las correcciones**: cuando alguien resuelve a mano un "no lo encontré", ese apodo queda
 * guardado y la próxima vez se reconoce solo. Cero configuración.
 *
 * No usa IA ni servicios externos: el mensaje del cliente lleva su nombre, su dirección y a veces
 * su teléfono, y eso no sale de la computadora del negocio.
 */

/** Un tamaño concreto en el que se vende algo. Es lo que acaba siendo un renglón del pedido. */
export interface FormatoParaLeer {
  productoId: number;
  /** Nombre completo del catálogo: "Frijoles Charros 1/2 litro". */
  nombre: string;
  /** Cuánto de la unidad base lleva: 1, 0.5, 0.25… */
  cantidad: number;
  precio: number;
  /** `false` = agotado. Se reconoce igual y se avisa: el cliente lo pidió. */
  disponible?: boolean;
}

/** Lo que el negocio vende, como lo capturó: un producto con sus tamaños (Sprint 84). */
export interface GrupoParaLeer {
  /** Nombre base, sin tamaño: "Frijoles Charros". */
  nombre: string;
  /** 'kg' | 'litro' | 'pieza'. Sirve para redactar la pregunta del tamaño. */
  unidad?: string;
  formatos: FormatoParaLeer[];
}

/** Un apodo aprendido: cómo le dicen los clientes a un producto concreto. */
export interface AliasParaLeer {
  /** Como lo escribe la gente: "coca grande", "espadas", "frijolitos". */
  texto: string;
  productoId: number;
}

/** Una línea que el sistema reconoció con confianza. */
export interface LineaLeida {
  productoId: number;
  nombre: string;
  cantidad: number;
  precio: number;
  /** Fragmento del mensaje que la produjo, para que el operador pueda contrastar. */
  origen: string;
  /** Reconocido pero agotado: se muestra en rojo y se ofrece sustituto. */
  agotado?: boolean;
  /** Se resolvió por un apodo aprendido. Se dice en pantalla: nadie confía en lo que no entiende. */
  porApodo?: boolean;
}

/** Algo que se mencionó y no se pudo resolver solo. */
export interface DudaLeida {
  /** El pedazo del mensaje que la provocó, tal cual lo escribió el cliente. */
  texto: string;
  motivo: 'sin_cantidad' | 'que_tamano' | 'no_reconocido';
  /**
   * El producto del que se habla, cuando se sabe cuál es. Sirve para redactar la pregunta:
   * "Frijoles Charros — ¿de qué tamaño?" en vez de un genérico "no entendí".
   */
  producto?: string;
  unidad?: string;
  /** Qué ofrecerle al operador para resolverla de un toque. */
  opciones: Array<{ productoId: number; nombre: string; precio: number; cantidad: number; agotado?: boolean }>;
}

export interface PedidoLeido {
  lineas: LineaLeida[];
  dudas: DudaLeida[];
}

/** Números escritos con letra, que es como pide media clientela. */
const NUMEROS: Record<string, number> = {
  un: 1, una: 1, uno: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5, seis: 6,
  siete: 7, ocho: 8, nueve: 9, diez: 10, once: 11, doce: 12,
  par: 2, docena: 12,
};

/**
 * Palabras que no aportan al pedido y ensucian la comparación.
 *
 * Se quitan **sólo para comparar**; el texto original se conserva para mostrárselo al operador.
 */
const RUIDO = new Set([
  'hola', 'buenas', 'tardes', 'noches', 'dias', 'días', 'buenos', 'porfa', 'porfavor', 'favor',
  'quiero', 'quisiera', 'me', 'da', 'das', 'dame', 'mandame', 'mándame', 'manda', 'mandas',
  'para', 'llevar', 'domicilio', 'gracias', 'por', 'y', 'de', 'del', 'la', 'el', 'los', 'las',
  'un', 'una', 'unos', 'unas', 'con', 'sin', 'mi', 'su', 'es', 'son', 'que', 'qué', 'al', 'lo',
  'ok', 'si', 'sí', 'tambien', 'también', 'ademas', 'además', 'pedido', 'orden', 'seria', 'sería',
  'serian', 'serían', 'ponme', 'echame', 'échame', 'mandarme', 'favorcito', 'seran', 'serán',
]);

export function normalizar(t: string): string {
  return t
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\w\s/.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Quita el plural y los diminutivos, que es como habla la gente: "pollitos" → "poll". */
function raiz(w: string): string {
  let r = w;
  if (r.length > 5) r = r.replace(/(itos|itas|ito|ita)$/, '');
  if (r.length > 3) r = r.replace(/e?s$/, '');
  return r;
}

function palabrasUtiles(t: string): string[] {
  return normalizar(t)
    .split(' ')
    // Las fracciones ("1/2") no son palabras: son tamaños y se comparan aparte.
    .filter((p) => p.length > 2 && !RUIDO.has(p) && !/^\d+([/.]\d+)?$/.test(p));
}

/**
 * Qué tanto se parece un fragmento al nombre de un producto.
 *
 * Es a propósito **simple y explicable**: cuántas de las palabras con contenido del producto
 * aparecen en el fragmento, comparando por raíz. Un puntaje sofisticado sería más difícil de
 * justificar cuando el operador pregunte *"¿por qué me pusiste esto?"* — y aquí el sistema siempre
 * tiene que poder responder eso.
 */
function parecido(fragmento: string, nombre: string): number {
  const frag = normalizar(fragmento).split(' ').map(raiz);
  const clave = palabrasUtiles(nombre).map(raiz);
  if (clave.length === 0) return 0;
  const aciertos = clave.filter((p) => frag.includes(p)).length;
  return aciertos / clave.length;
}

/**
 * ¿Cuántos pidió? Se busca **después de descontar las palabras del producto**.
 *
 * Ése es el detalle que decide si esto sirve o estorba: *"1 coca cola de 3 litros"* tiene dos
 * números y sólo uno es la cantidad. Buscar "el primer número" daría 1 aquí y **3** en *"coca cola
 * de 3 litros"* — o sea, un cliente pidiendo una y recibiendo tres.
 */
function cantidadEn(fragmento: string, nombreProducto: string): number | null {
  const delProducto = new Set(normalizar(nombreProducto).split(' ').filter(Boolean));
  const raicesProducto = new Set([...delProducto].map(raiz));

  const sobrante = normalizar(fragmento)
    .split(' ')
    .filter((w) => w && !delProducto.has(w) && !raicesProducto.has(raiz(w)));

  for (let i = 0; i < sobrante.length; i++) {
    const w = sobrante[i];
    if (/^\d{1,4}$/.test(w)) return Number(w);
    const porEne = w.match(/^x(\d{1,4})$/);
    if (porEne) return Number(porEne[1]);
    if (NUMEROS[w] !== undefined) {
      /**
       * "un par de pollos", "una docena de panes": el artículo va pegado al número de verdad y no
       * es la cantidad. Sin esto, *"un par"* devuelve **uno** — y el cliente recibe la mitad.
       */
      const siguiente = sobrante[i + 1];
      const esArticulo = w === 'un' || w === 'una' || w === 'uno';
      if (esArticulo && siguiente && NUMEROS[siguiente] !== undefined) continue;
      return NUMEROS[w];
    }
  }
  const suelto = sobrante.join(' ').match(/\bx\s(\d{1,4})\b/);
  if (suelto) return Number(suelto[1]);
  return null;
}

/**
 * ¿El fragmento dice de qué tamaño, dentro de este grupo?
 *
 * Lo que separa a los formatos de un mismo producto son unas pocas palabras ("1 litro" vs "1/2
 * litro"). Si el cliente escribió una de ésas, ya eligió él y no hay nada que preguntar. Se compara
 * contra **el sufijo** de cada nombre —lo que le sobra al nombre del grupo—, que es exactamente el
 * tamaño.
 */
function formatoQuePidio(
  fragmento: string,
  nombreGrupo: string,
  formatos: FormatoParaLeer[],
): FormatoParaLeer | null {
  const frag = normalizar(fragmento);
  const base = normalizar(nombreGrupo);
  const fragTokens = new Set(frag.split(' '));

  const candidatos = formatos.map((f) => {
    const completo = normalizar(f.nombre);
    const sufijo = completo.startsWith(base) ? completo.slice(base.length).trim() : completo;
    return { f, tokens: sufijo.split(' ').filter(Boolean) };
  });

  let mejor: { f: FormatoParaLeer; puntos: number } | null = null;
  for (const c of candidatos) {
    if (c.tokens.length === 0) continue;
    // Sólo cuentan los tokens que **distinguen**: los que no aparecen en todos los formatos.
    const distintivos = c.tokens.filter((t) =>
      candidatos.some((otro) => otro !== c && !otro.tokens.includes(t)),
    );
    const puntos = distintivos.filter((t) => fragTokens.has(t)).length;
    if (puntos > 0 && (!mejor || puntos > mejor.puntos)) mejor = { f: c.f, puntos };
  }
  return mejor?.f ?? null;
}

/**
 * Parte el mensaje en pedazos que puedan ser una línea de pedido.
 *
 * Se corta por saltos de línea, comas, "y", "más" y viñetas: es como la gente separa las cosas
 * cuando escribe rápido.
 */
export function partirEnFragmentos(texto: string): string[] {
  return texto
    .split(/[\n,;]+|\s+y\s+|\s+mas\s+|\s+más\s+|\s+\+\s+|•|·/gi)
    .map((f) => f.trim())
    .filter((f) => f.length > 1);
}

/** Interpreta el mensaje contra lo que el negocio vende, agrupado como lo capturó. */
export function leerPedidoDeChat(
  texto: string,
  grupos: GrupoParaLeer[],
  alias: AliasParaLeer[] = [],
  /**
   * Sprint 98 — Líneas que el otro intérprete ya consumió como datos del cliente.
   *
   * Nombre, teléfono, dirección y referencias. No son pedido, y preguntarlas como si lo fueran es
   * lo que llenaba la pantalla de tarjetas *"No encontré esto en tu menú"* con pedazos del
   * domicilio del cliente — mientras la misma ventana, cuatro centímetros más arriba, mostraba esa
   * dirección como leída correctamente.
   *
   * Sólo se usan para **no preguntar**: si en una de esas líneas hubiera un producto reconocible,
   * el producto se reconoce igual. El filtro actúa en el único punto donde nacería la duda.
   */
  lineasDeDatos: string[] = [],
): PedidoLeido {
  const lineas: LineaLeida[] = [];
  const dudas: DudaLeida[] = [];
  if (!texto?.trim() || grupos.length === 0) return { lineas, dudas };

  // Índice de apodos y de dónde vive cada formato, para no recorrer todo en cada fragmento.
  const porApodo = new Map<string, number>();
  for (const a of alias) {
    const k = normalizar(a.texto);
    if (k) porApodo.set(k, a.productoId);
  }
  const formatoPorId = new Map<number, { g: GrupoParaLeer; f: FormatoParaLeer }>();
  for (const g of grupos) for (const f of g.formatos) formatoPorId.set(f.productoId, { g, f });

  const sumarLinea = (l: LineaLeida) => {
    const ya = lineas.find((x) => x.productoId === l.productoId);
    if (ya) {
      ya.cantidad += l.cantidad;
      ya.origen = `${ya.origen} · ${l.origen}`;
      return;
    }
    lineas.push(l);
  };

  for (const fragmento of partirEnFragmentos(texto)) {
    if (palabrasUtiles(fragmento).length === 0) continue; // saludo, no renglón

    /**
     * Los apodos van primero y ganan: son una corrección que una persona ya hizo a mano. Si alguien
     * enseñó que "coca grande" es la de 3 litros, volver a preguntarlo sería no haber aprendido.
     */
    const claveApodo = [...porApodo.keys()].find((k) => normalizar(fragmento).includes(k));
    if (claveApodo) {
      const ref = formatoPorId.get(porApodo.get(claveApodo)!);
      if (ref) {
        const cantidad = cantidadEn(fragmento, `${ref.g.nombre} ${claveApodo}`) ?? 1;
        sumarLinea({
          productoId: ref.f.productoId,
          nombre: ref.f.nombre,
          cantidad,
          precio: ref.f.precio,
          origen: fragmento,
          porApodo: true,
          ...(ref.f.disponible === false ? { agotado: true } : {}),
        });
        continue;
      }
    }

    const puntuados = grupos
      .map((g) => ({ g, score: parecido(fragmento, g.nombre) }))
      .filter((x) => x.score >= 0.5)
      .sort((a, b) => b.score - a.score);

    if (puntuados.length === 0) {
      // Si esto salió de la dirección o del nombre, no es que "no lo encontremos": es que no es
      // comida. Preguntarlo sería obligar al operador a descartar su propio dato cinco veces.
      if (vieneDeUnDato(fragmento, lineasDeDatos)) continue;
      dudas.push({ texto: fragmento, motivo: 'no_reconocido', opciones: [] });
      continue;
    }

    const grupo = puntuados[0].g;
    const cantidad = cantidadEn(fragmento, grupo.nombre);

    /**
     * De qué tamaño. Con un solo formato **no hay nada que preguntar**: es la diferencia entre una
     * pregunta del negocio y una pregunta de nuestra base de datos.
     */
    const elegido =
      grupo.formatos.length === 1
        ? grupo.formatos[0]
        : formatoQuePidio(fragmento, grupo.nombre, grupo.formatos);

    if (!elegido) {
      dudas.push({
        texto: fragmento,
        motivo: 'que_tamano',
        producto: grupo.nombre,
        unidad: grupo.unidad,
        opciones: grupo.formatos.map((f) => ({
          productoId: f.productoId,
          nombre: f.nombre,
          precio: f.precio,
          cantidad: cantidad ?? 1,
          ...(f.disponible === false ? { agotado: true } : {}),
        })),
      });
      continue;
    }

    /**
     * Reconocido pero sin cantidad. No se asume 1: *"mándame pollo"* puede ser uno o pueden ser los
     * tres de siempre, y el que sabe es quien está leyendo el chat.
     */
    if (cantidad === null) {
      dudas.push({
        texto: fragmento,
        motivo: 'sin_cantidad',
        producto: elegido.nombre,
        unidad: grupo.unidad,
        opciones: [1, 2, 3, 4].map((c) => ({
          productoId: elegido.productoId,
          nombre: elegido.nombre,
          precio: elegido.precio,
          cantidad: c,
          ...(elegido.disponible === false ? { agotado: true } : {}),
        })),
      });
      continue;
    }

    sumarLinea({
      productoId: elegido.productoId,
      nombre: elegido.nombre,
      cantidad,
      precio: elegido.precio,
      origen: fragmento,
      ...(elegido.disponible === false ? { agotado: true } : {}),
    });
  }

  return { lineas, dudas };
}

/** Lo que suma lo reconocido, para enseñarlo antes de confirmar. */
export function totalLeido(p: { lineas: LineaLeida[] }): number {
  return p.lineas.reduce((s, l) => s + l.precio * l.cantidad, 0);
}

/**
 * ¿Vale la pena guardar este texto como apodo?
 *
 * Se guarda **lo que el cliente escribió**, limpio de ruido y de cantidades. "2 cocas grandes" se
 * aprende como "cocas grandes", no con el número — si no, el apodo sólo serviría para pedidos de
 * dos. Devuelve `null` cuando no queda nada que valga la pena (un número suelto, un saludo).
 */
export function apodoDesde(texto: string): string | null {
  const limpio = palabrasUtiles(texto).join(' ').trim();
  if (limpio.length < 3) return null;
  /**
   * Cuatro palabras ya no son un apodo, son una frase — *"algo rico para la cena"*— y no volvería a
   * coincidir nunca. Los apodos reales son de una a tres: "espadas", "coca grande", "alitas bbq".
   */
  if (limpio.split(' ').length > 3) return null;
  return limpio;
}

/**
 * Sprint 98 — ¿Este fragmento salió de una línea que ya se usó como dato del cliente?
 *
 * Los fragmentos se cortan por comas, así que "Fracc. Galaxia" es un pedazo de la línea
 * "Domicilio: Jupiter 206, Fracc. Galaxia, Tabasco 2000, C.P. 86035". Se compara normalizado y por
 * contención: basta con que el pedazo viva dentro de una de esas líneas.
 */
function vieneDeUnDato(fragmento: string, lineasDeDatos: string[]): boolean {
  if (lineasDeDatos.length === 0) return false;
  const f = normalizar(fragmento);
  if (!f) return false;
  return lineasDeDatos.some((l) => normalizar(l).includes(f));
}
